import { doorSearch } from "../../lib/chat";
import { useOpen, beacon } from "../../lib/beacon";
import React, { useState } from "react";
import { BoardCard, RowS } from "./Card";
import { STATUS_OPTIONS, sectionTitle } from "./model";
import { fmtDay, plural } from "../../lib/vikas";
import { MapBoard } from "../map";

export function Tiles({ tiles, usage, tile, setTile }) {
  if (!tiles?.length) return null;
  return (
    <div className="tiles" data-testid="tiles">
      {tiles.map((t) => {
        const isUsage = t.key === "usage";
        const nm = isUsage && usage?.stale;
        const sub = t.sub || (isUsage && usage?.read_at ? `${nm ? "last read" : "read"} ${fmtDay(String(usage.read_at).slice(0, 10))}` : "");
        const alert = t.key === "late" && Number(t.value) > 0;
        return (
          <button key={t.key} className={`tile${tile === t.key ? " on" : ""}${alert ? " alert" : ""}${nm ? " nm" : ""}`}
            aria-pressed={tile === t.key} data-testid={`tile-${t.key}`} onClick={() => setTile(tile === t.key ? "" : t.key)}>
            <span className="n">{nm ? "NOT MEASURED" : t.value}</span>
            <span className="nm-t">{t.label}</span>
            {sub ? <span className="sub">{sub}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

const RANK = { late: 0, waiting: 1 };

export function Yours({ ids, cards, onOpen, pressOf, onAll, onTopic }) {
  // onTopic null = the queue mode: no per-topic counts
  useOpen("yours", Boolean(ids && ids.length));
  if (!ids) return null;
  const rows = ids.map((id) => cards[id]).filter((c) => c && !c.team_only)
    .map((c, i) => [c, i]).sort((a, b) => (RANK[a[0].state] ?? 2) - (RANK[b[0].state] ?? 2) || a[1] - b[1]).map(([c]) => c);
  const more = rows.length - 5;
  if (!rows.length) return null;
  return (
    <div className="yours" data-testid="yours-today">
      <div className="yh"><span className="label">Yours today</span></div>
      {rows.length > 0 && onTopic && (
        <div className="ytopics" data-testid="yours-topics">
          {Object.entries(rows.reduce((m, c) => ({ ...m, [c.topic || "Other"]: (m[c.topic || "Other"] || 0) + 1 }), {}))
            .sort((x, y) => y[1] - x[1])
            .map(([t, n], i) => (
              <React.Fragment key={t}>
                {i > 0 && <span className="faint"> · </span>}
                <button className="ytopic" data-testid={`yours-topic-${t}`} onClick={() => onTopic(t)}>{t} {n}</button>
              </React.Fragment>
            ))}
        </div>
      )}
      {rows.slice(0, 5).map((c) => <RowS key={c.id} c={c} chip={c.topic} onOpen={onOpen} press={pressOf(c.id)} testid={`yours-${c.id}`} />)}
      {more > 0 && <button className="btn ghost more" data-testid="yours-show-all" onClick={onAll}>{onTopic ? `+${more} more — show all` : "Show all"}</button>}
    </div>
  );
}

const DATES = [["today", "Today"], ["7", "7 days"], ["30", "30 days"], ["custom", "Custom"]];

export function Filters({ f, setF, people, topics, privates = [], onFoldAll, onOpenAll }) {
  const [open, setOpen] = useState(false);
  const up = (p) => setF({ ...f, ...p });
  const active = ["mine", "owner", "topic", "status", "date", "tile"].filter((k) => f[k]).length;
  return (
    <div className={`filters${open ? " open" : ""}`} data-testid="filters">
      <div className="fset">
        <button className={`chip${!f.mine && !f.owner ? " on" : ""}`} data-testid="filter-all" onClick={() => up({ mine: false, owner: "", topic: "", status: "", q: "", date: "", tile: "" })}>All</button>
        <button className={`chip${f.mine ? " on" : ""}`} data-testid="filter-mine" onClick={() => up({ mine: !f.mine, owner: "" })}>My cards</button>
        <label className="chip own"><span className="faint">Owner:</span>
          <select value={f.owner} onChange={(e) => up({ owner: e.target.value, mine: false })} data-testid="filter-owner" aria-label="Owner">
            <option value="">Everyone</option>
            {people.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        {topics.map((t) => (
          <button key={t.key} className={`chip${f.topic === t.key ? " on" : ""}`} data-testid={`filter-topic-${t.key}`}
            onClick={() => up({ topic: f.topic === t.key ? "" : t.key })}>{sectionTitle(t)}</button>
        ))}
        {privates.map((k) => (
          <button key={k} className={`chip${f.topic === k ? " on" : ""}`} data-testid={`filter-topic-${k}`}
            onClick={() => up({ topic: f.topic === k ? "" : k })}>{k}</button>
        ))}
        <label className="chip own">
          <select value={f.status} onChange={(e) => up({ status: e.target.value })} data-testid="filter-status" aria-label="Status">
            <option value="">Any status</option>
            {STATUS_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
      </div>
      <input className="search" type="search" placeholder="Search cards, ids, files" value={f.q} onChange={(e) => up({ q: e.target.value })} data-testid="filter-search" />
      <button className={`chip fbtn${active ? " on" : ""}`} aria-expanded={open} data-testid="filters-toggle" onClick={() => setOpen(!open)}>
        Filters{active ? ` · ${active}` : ""} {open ? "▴" : "▾"}
      </button>
      <div className="fset">
        {DATES.map(([k, l]) => (
          <button key={k} className={`chip${f.date === k ? " on" : ""}`} data-testid={`filter-date-${k}`} onClick={() => up({ date: f.date === k ? "" : k })}>{l}</button>
        ))}
        <button className="chip" data-testid="fold-all" onClick={onFoldAll}>Fold all</button>
        <button className="chip" data-testid="open-all" onClick={onOpenAll}>Open all</button>
        {f.date === "custom" && (
          <span className="custom-dates">
            <input type="date" className="vinput" value={f.from || ""} onChange={(e) => up({ from: e.target.value })} data-testid="filter-from" aria-label="From" />
            <input type="date" className="vinput" value={f.to || ""} onChange={(e) => up({ to: e.target.value })} data-testid="filter-to" aria-label="To" />
          </span>
        )}
      </div>
    </div>
  );
}

function Counts({ k }) {
  if (!k) return null;
  const parts = [["open", k.open], ["late", k.late], ["waiting", k.waiting], ["stale", k.stale]].filter(([n, v]) => n === "open" || v > 0);
  return (
    <span className="counts">
      {parts.map(([n, v], i) => <React.Fragment key={n}>{i > 0 && " · "}{n === "late" ? <b>{v} late</b> : `${v ?? 0} ${n}`}</React.Fragment>)}
    </span>
  );
}

export function SecHead({ s, extra, fold }) {
  return (
    <div className="sec-h">
      {fold && (
        <button className={`fold${fold.folded ? " shut" : ""}`} aria-expanded={!fold.folded} aria-label={`${fold.folded ? "Unfold" : "Fold"} ${sectionTitle(s)}`}
          data-testid={`fold-${s.key}`} onClick={fold.toggle}>▾</button>
      )}
      <h2>{sectionTitle(s)}</h2>
      {s.scope ? <span className="scope">{s.scope}</span> : null}
      {extra || <Counts k={s.counts} />}
    </div>
  );
}

const ORDER_RANK = (c, need) => (c.state === "late" ? 0 : need.has(c.id) ? 1 : c.state === "waiting" ? 2 : 3);
export const orderCards = (list, need) =>
  list.map((c, i) => [c, i]).sort((x, y) => ORDER_RANK(x[0], need) - ORDER_RANK(y[0], need) || x[1] - y[1]).map(([c]) => c);

function Tally({ list, need, testid }) {
  const nNeed = list.filter((c) => need.has(c.id)).length;
  const nLate = list.filter((c) => c.state === "late").length;
  return (
    <span className="tally" data-testid={testid}>
      <span>{list.length}</span>
      {nNeed > 0 && !need.quiet && <span className="t-need">{nNeed} need you</span>}
      {nLate > 0 && <span className="t-late">{nLate} late</span>}
    </span>
  );
}

function FoldBtn({ shut, onClick, label, testid }) {
  return <button className={`fold${shut ? " shut" : ""}`} aria-expanded={!shut} aria-label={`${shut ? "Open" : "Fold"} ${label}`} data-testid={testid} onClick={onClick}>▾</button>;
}

// a group of cards: header row, first 3 then "Show all N"; a filter opens matching groups and folds the rest
function Group({ id, label, all, shown, need, folds, filtering, view, testid }) {
  const [more, setMore] = useState(false);
  const shut = filtering ? shown.length === 0 : folds.isShut(id);
  const list = filtering ? shown : orderCards(all, need);
  const vis = filtering || more || list.length <= 3 ? list : list.slice(0, 3);
  return (
    <div className={`sgc${shut ? " shut" : ""}`} data-testid={testid}>
      <div className="sg-h">
        <FoldBtn shut={shut} label={label} testid={`${testid}-fold`} onClick={() => folds.toggle(id)} />
        <span className="label">{label}</span>
        <Tally list={all} need={need} testid={`${testid}-tally`} />
      </div>
      {!shut && vis.map((c) => view(c))}
      {!shut && vis.length < list.length && (
        <button className="btn ghost more" data-testid={`${testid}-show-all`} onClick={() => setMore(true)}>Show all {list.length}</button>
      )}
    </div>
  );
}

export function TopicSection({ s, cards, show, filtering, view, folds, need }) {
  useOpen(s.key);
  const live = (ids) => (ids || []).map((id) => cards[id]).filter((c) => c && c.state !== "closed" && !c.team_only && c.weight !== "housekeeping");
  const all = live(s.card_ids);
  const blankLane = !filtering && (s.counts?.open || 0) > 0 && all.length === 0;
  React.useEffect(() => { if (blankLane) beacon("blank", s.key, { reason: "count_no_cards" }); }, [blankLane, s.key]);
  const inSub = new Set((s.subgroups || []).flatMap((g) => g.card_ids || []));
  const groups = (s.subgroups || []).map((g) => ({ key: g.key, label: g.label, all: live(g.card_ids) }));
  const rest = all.filter((c) => !inSub.has(c.id));
  if (rest.length) groups.push({ key: "__other", label: groups.length ? "Other" : "", all: rest });
  const matches = all.filter(show);
  const tid = `T:${s.key}`;
  const shut = filtering ? matches.length === 0 : folds.isShut(tid);
  return (
    <section className={`sec${shut ? " folded" : ""}`} data-testid={`section-${s.key}`}>
      <div className="sec-h">
        <FoldBtn shut={shut} label={sectionTitle(s)} testid={`fold-${s.key}`} onClick={() => folds.toggle(tid)} />
        <h2>{sectionTitle(s)}</h2>
        {s.scope ? <span className="scope">{s.scope}</span> : null}
        <Tally list={all} need={need} testid={`tally-${s.key}`} />
      </div>
      {shut ? null : (s.card_ids || []).length === 0 ? <div className="faint small sec-empty">No cards yet</div>
        : filtering && matches.length === 0 ? <div className="faint small sec-empty">Nothing here for this filter</div>
        : (
          <div className="sgs">
            {groups.filter((g) => g.label || g.all.length).map((g) => (
              g.label ? (
                <Group key={g.key} id={`G:${s.key}:${g.key}`} label={g.label} all={g.all} shown={g.all.filter(show)} need={need}
                  folds={folds} filtering={filtering} view={view} testid={`subgroup-${s.key}-${g.key}`} />
              ) : (
                <UngroupedGrid key={g.key} list={filtering ? g.all.filter(show) : orderCards(g.all, need)} view={view} testid={`grid-${s.key}`} filtering={filtering} />
              )
            ))}
          </div>
        )}
    </section>
  );
}

function UngroupedGrid({ list, view, testid, filtering }) {
  const [more, setMore] = useState(false);
  const vis = filtering || more || list.length <= 3 ? list : list.slice(0, 3);
  return (
    <>
      {vis.map((c) => <div key={c.id} className="sgc">{view(c)}</div>)}
      {vis.length < list.length && <button className="btn ghost more sg-more" data-testid={`${testid}-show-all`} onClick={() => setMore(true)}>Show all {list.length}</button>}
    </>
  );
}

export function RowsSection({ s, rows, title, right, onOpen, pressOf, filtering, limit }) {
  useOpen(s.key);
  const [all, setAll] = useState(false);
  const shown = limit && !all ? rows.slice(0, limit) : rows;
  return (
    <section className="sec" data-testid={`section-${s.key}`}>
      <SecHead s={{ ...s, title: title || sectionTitle(s) }} extra={<span className="counts">{shown.length < rows.length ? `${shown.length} of ${rows.length}` : rows.length} shown</span>} />
      <div className="closedlist">
        {rows.length === 0 && <div className="faint small">{filtering ? "Nothing here for this filter" : "No cards yet"}</div>}
        {shown.map((c) => <RowS key={c.id} c={c} right={right(c)} onOpen={onOpen} press={pressOf(c.id)} testid={`row-${s.key}-${c.id}`} />)}
        {shown.length < rows.length && <button className="btn ghost more" data-testid={`show-all-${s.key}`} onClick={() => setAll(true)}>Show all {rows.length}</button>}
      </div>
    </section>
  );
}

function Shelf({ sh, cards, show, onOpen, onPdf, folds, filtering }) {
  const [waitOpen, setWaitOpen] = useState(false);
  const docs = (sh.card_ids || []).map((id) => cards[id]).filter((c) => c && show(c));
  const withPdf = docs.filter((c) => c.file);
  const noPdf = docs.filter((c) => !c.file);
  const id = `B:${sh.key}`;
  const shut = filtering ? docs.length === 0 : folds.isShut(id);
  const row = (c) => (
    <li key={c.id} className={c.file ? "" : "no"} data-testid={`shelf-doc-${c.id}`}>
      <button className="doc" disabled={!c.file} onClick={() => onPdf(c)} data-testid={`shelf-pdf-${c.id}`}>{c.title}</button>
      {!c.file && <span className="nopdf">No PDF on the board yet</span>}
      <button className="btn ghost tiny" data-opens-card onClick={() => onOpen(c)} data-testid={`shelf-card-${c.id}`}>Card</button>
    </li>
  );
  return (
    <div className={`shelf${shut ? " shut" : ""}`} data-testid={`shelf-${sh.key}`}>
      <h3>
        <FoldBtn shut={shut} label={sh.title} testid={`shelf-${sh.key}-fold`} onClick={() => folds.toggle(id)} />
        {sh.title} <span className="faint small">· {sh.count ?? (sh.card_ids || []).length}</span>
      </h3>
      {!shut && (
        <ul>
          {withPdf.map(row)}
          {noPdf.length > 0 && (
            <li className="no waitline">
              <button className="doc" aria-expanded={waitOpen} data-testid={`shelf-${sh.key}-waiting`} onClick={() => setWaitOpen(!waitOpen)}>
                {noPdf.length} waiting for a PDF {waitOpen ? "▾" : "▸"}
              </button>
            </li>
          )}
          {waitOpen && noPdf.map(row)}
        </ul>
      )}
    </div>
  );
}

function ShelfSearch() {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState(null);
  const [msg, setMsg] = useState("");
  const go = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setMsg("Searching…"); setHits(null);
    try {
      const h = await doorSearch(q.trim());
      if (h === null) setMsg("Search is not set up yet");
      else { setHits(h); setMsg(h.length ? "" : "Nothing found on the shelves"); }
    } catch (err) { setMsg(err.message); }
  };
  return (
    <div className="shelf-search" data-testid="shelf-search">
      <form onSubmit={go}>
        <input className="vk-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search inside the documents" aria-label="Search inside the documents" data-testid="shelf-search-input" />
        <button className="btn ghost tiny" type="submit" data-testid="shelf-search-go">Search</button>
      </form>
      {msg && <div className="faint small" data-testid="shelf-search-msg">{msg}</div>}
      {hits && hits.length > 0 && (
        <ul className="shelf-hits" data-testid="shelf-search-hits">
          {hits.map((h, i) => (
            <li key={i}><span className="label">{h.shelf}</span> <span>{h.doc}</span>{h.section ? <span className="faint"> · {h.section}</span> : null}<div className="faint small">{h.line}</div></li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Bookshelf({ s, cards, show, onOpen, onPdf, folds, filtering }) {
  useOpen(s.key);
  const shelves = s.shelves || [];
  const total = shelves.reduce((a, sh) => a + (sh.card_ids || []).length, 0);
  const noPdf = shelves.reduce((a, sh) => a + (sh.card_ids || []).filter((id) => cards[id] && !cards[id].file).length, 0);
  return (
    <section className="sec" data-testid={`section-${s.key}`}>
      <SecHead s={s} extra={<span className="counts">{plural(total, "document", "documents")} · {noPdf} {noPdf === 1 ? "has" : "have"} no PDF on the board yet</span>} />
      <ShelfSearch />
      <div className="shelves">
        {shelves.map((sh) => <Shelf key={sh.key} sh={sh} cards={cards} show={show} onOpen={onOpen} onPdf={onPdf} folds={folds} filtering={filtering} />)}
      </div>
    </section>
  );
}

export function Team({ s, owner, setOwner, cards = {}, view }) {
  useOpen(s.key);
  const asks = (s.card_ids || []).map((id) => cards[id]).filter((c) => c && c.team_only);
  return (
    <section className="sec" data-testid={`section-${s.key}`}>
      <SecHead s={s} extra={<span />} />
      <div className="team">
        {(s.people || []).map((p) => {
          const name = typeof p === "string" ? p : p.name;
          const own = asks.filter((c) => c.owner === name);
          const chip = (
            <button key={name} className={`pchip${owner === name ? " on" : ""}`} data-testid={`team-${name}`} onClick={() => setOwner(owner === name ? "" : name)}>
              <span className="avatar">{name[0]}</span>{name}{p.open != null ? ` · ${p.open} open` : ""}
              {p.late > 0 ? <> · <span className="late">{p.late} late</span></> : null}
            </button>
          );
          if (!own.length || !view) return chip;
          return <div key={name} className="tperson" data-testid={`team-asks-${name}`}>{chip}<div className="tasks">{own.map(view)}</div></div>;
        })}
      </div>
    </section>
  );
}

export function FooterFold({ s, data, ctx }) {
  const [open, setOpen] = useState(false);
  const metaSecs = data.meta?.sections || [];
  const keys = metaSecs.filter((m) => (m.panels || []).some((p) => (s.panels || []).includes(p))).map((m) => m.key);
  return (
    <div data-testid={`section-${s.key}`}>
      <button className="footer-fold" aria-expanded={open} data-testid="footer-fold-toggle" onClick={() => setOpen((o) => !o)}>
        <span>More · who does what · cost and usage · ideas · keys</span>
        <span className="faint">{open ? "open ▾" : "folded ▸"}</span>
      </button>
      {open && <div className="footer-open"><MapBoard data={data} ctx={ctx} only={keys} /></div>}
    </div>
  );
}
